import { useState, type FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, LogIn, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/features/auth/AuthContext';
import { useLanguage } from '@/hooks/useLanguage';
import { ApiError } from '@/lib/api';
import { Button, Field, Input } from '@/components/ui';
import { Logo } from '@/components/layout/Logo';
import { LanguageSwitcher } from '@/components/layout/LanguageSwitcher';
import { ThemeToggle } from '@/components/layout/ThemeToggle';

export function LoginPage() {
  const { t } = useLanguage();
  const { signIn, isAuthenticated, initializing } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!initializing && isAuthenticated) {
    const from = (location.state as { from?: string } | null)?.from ?? '/dashboard';
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const user = await signIn(email.trim().toLowerCase(), password);
      navigate(user.mustChangePassword ? '/change-password' : '/dashboard', { replace: true });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.localized() : t('errors.network'));
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Colonne institutionnelle */}
      <section className="relative hidden flex-col justify-between overflow-hidden bg-primary p-12 text-primary-ink lg:flex">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 20%, white 1.5px, transparent 1.5px), radial-gradient(circle at 70% 60%, white 1.5px, transparent 1.5px)',
            backgroundSize: '48px 48px',
          }}
          aria-hidden
        />

        <div className="relative flex items-center gap-3">
          <Logo size={44} className="[&_rect]:fill-white/15 [&_path]:fill-white [&_circle]:fill-accent" />
          <div>
            <p className="font-display text-xl font-bold">{t('app.name')}</p>
            <p className="text-xs uppercase tracking-[0.18em] opacity-75">{t('app.institution')}</p>
          </div>
        </div>

        <div className="relative max-w-lg space-y-6">
          <h1 className="font-display text-4xl font-bold leading-tight">{t('app.tagline')}</h1>
          <p className="text-base leading-relaxed opacity-85">
            {t('app.country')} — {t('app.motto')}
          </p>
          <dl className="grid grid-cols-3 gap-4 pt-4">
            {[
              { value: '4', label: t('learn.modules') },
              { value: '20', label: t('learn.sessions') },
              { value: '2 000', label: t('admin.kpi.learners') },
            ].map((item) => (
              <div key={item.label} className="rounded-2xl bg-white/10 px-4 py-3 backdrop-blur-sm">
                <dt className="font-display text-2xl font-bold">{item.value}</dt>
                <dd className="text-xs opacity-80">{item.label}</dd>
              </div>
            ))}
          </dl>
        </div>

        <p className="relative flex items-center gap-2 text-xs opacity-70">
          <ShieldCheck className="h-4 w-4" aria-hidden />
          {t('auth.securityNotice')}
        </p>
      </section>

      {/* Colonne formulaire */}
      <section className="flex flex-col justify-center bg-background px-5 py-10 sm:px-10">
        <div className="mx-auto w-full max-w-md">
          <div className="mb-8 flex items-center justify-between">
            <div className="flex items-center gap-2.5 lg:hidden">
              <Logo size={34} />
              <span className="font-display text-lg font-bold text-ink">{t('app.name')}</span>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <LanguageSwitcher compact />
              <ThemeToggle />
            </div>
          </div>

          <div className="mb-7 space-y-2">
            <h2 className="font-display text-2xl font-bold text-ink">{t('auth.signInTitle')}</h2>
            <p className="text-sm text-ink-muted">{t('auth.signInSubtitle')}</p>
          </div>

          <form onSubmit={(event) => void onSubmit(event)} className="space-y-4" noValidate>
            <Field label={t('auth.email')} htmlFor="email" required>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={t('auth.emailPlaceholder')}
                invalid={Boolean(error)}
              />
            </Field>

            <Field label={t('auth.password')} htmlFor="password" required>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder={t('auth.passwordPlaceholder')}
                  invalid={Boolean(error)}
                  className="pr-11"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                  className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-ink-subtle transition-colors hover:text-ink"
                >
                  {showPassword ? <EyeOff className="h-4.5 w-4.5" /> : <Eye className="h-4.5 w-4.5" />}
                </button>
              </div>
            </Field>

            {error ? (
              <p role="alert" className="rounded-xl bg-danger-soft px-4 py-3 text-sm font-medium text-danger">
                {error}
              </p>
            ) : null}

            <Button type="submit" size="lg" loading={submitting} icon={<LogIn className="h-4 w-4" />} className="w-full justify-center">
              {submitting ? t('auth.signingIn') : t('auth.signIn')}
            </Button>

            <p className="text-center text-sm">
              <Link to="/forgot-password" className="font-medium text-primary underline underline-offset-2">
                {t('auth.forgotPassword')}
              </Link>
            </p>
          </form>


          <p className="mt-6 text-center text-xs text-ink-muted">
            {t('auth.needHelp')}{' '}
            <Link to="/help" className="font-medium text-primary underline underline-offset-2">
              {t('help.title')}
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
}
